import React, { useCallback } from 'react';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Icon from '@mdi/react';
import { mdiLoading, mdiMagnify, mdiPencil, mdiPlus, mdiTrashCan } from '@mdi/js';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Modal from '@/components/layout/Modal';
import Descricao from '@/components/tipografia/Descricao';
import { useInfiniteQuery, useMutation } from 'react-query';
import axios from 'axios';
import { toastSuccess, toastWarning } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';
import { useDebounce } from '@/lib/useDebounce';
import { FormGrupoUsuario } from './FormGrupoUsuario';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

export const GruposManagerModal = ({ onClose }: { onClose: () => void }) => {
  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<
    string,
    string
  > | null>(null);

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  const queryList = useInfiniteQuery({
    queryKey: ['usuario_grupo', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        txt: debouncedSearch,
        c: pageParam,
      });

      return await axios.get<ResponseList>(
        `/api/usuario_grupo?${params.toString()}`,
        {
          withCredentials: true,
        },
      );
    },
    getNextPageParam: lastPage => lastPage.data.cursor ?? null,
  });

  const changeMutator = useMutation(
    async () => {
      if (!editObject) return;

      setFieldErrors(null);
      if (editObject.id) {
        return await axios.put(
          `/api/usuario_grupo/${editObject.id}`,
          editObject,
          {
            withCredentials: true,
          },
        );
      } else {
        return await axios.post(`/api/usuario_grupo`, editObject, {
          withCredentials: true,
        });
      }
    },
    {
      onSuccess: () => {
        setFieldErrors(null);
        queryList.refetch();
        setEditObject(null);
      },
      onError: (err: unknown) => {
        const { message, fieldErrors: errs } = extractApiError(err, 'Erro desconhecido');
        if (errs) setFieldErrors(errs);
        toastWarning(message);
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/usuario_grupo/${id}`, {
        withCredentials: true,
      });
    },
    {
      onSuccess: () => {
        toastSuccess('Grupo apagado com sucesso');
        queryList.refetch();
      },
      onError: (err: unknown) => {
        const { message } = extractApiError(err, 'Ocorreu um erro ao tentar apagar o grupo.');
        toastWarning(message);
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const handleChange = useCallback(async () => {
    await changeMutator.mutate();
  }, [changeMutator]);

  const handleDelete = (id: number) => {
    deleteMutator.mutate(id);
  };

  const handleCloseInner = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const headers: DataTableHeader[] = [
    { label: 'Nome', column: 'nome' },
    {
      label: 'Ações',
      width: '100px',
      stickRight: true,
      alignRight: true,
      render: (row: any) => (
        <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
          <Botao
            variant="none-primary"
            icon={mdiPencil}
            onClick={() => setEditObject(row)}
          />
          <BotaoPerigoso
            variant="none-danger"
            icon={mdiTrashCan}
            onClick={() => handleDelete(row.id)}
          />
        </HBox>
      ),
    },
  ];

  const queryFlatList =
    queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const editMode = !!editObject?.id && editObject.id > 0;

  return (
    <>
      <Modal show title="Grupos / Departamentos" onClose={onClose} size="lg">
        <VBox gap="12px" style={{ minWidth: '560px' }}>
          <Descricao>
            Gerencie seus grupos de usuários ou departamentos aqui
          </Descricao>

          <HBox gap="12px" style={{ alignItems: 'center' }}>
            <CampoTexto
              prepend={
                <Icon
                  path={mdiMagnify}
                  size={1}
                  color="var(--tc-color-gray-500)"
                />
              }
              placeholder="Pesquisar"
              value={search}
              onChange={setSearch}
            />
            <Botao
              variant="primary"
              icon={mdiPlus}
              onClick={() => setEditObject({})}
            >
              Novo grupo
            </Botao>
          </HBox>

          {queryList.isFetching && (
            <div>
              Aguarde...{' '}
              <Icon
                path={mdiLoading}
                size="14px"
                color="var(--tc-color-gray-700)"
                spin
              />
            </div>
          )}

          <DataTable
            isLoading={queryList.isFetching}
            cabecalhos={headers}
            linhas={queryFlatList ?? []}
            hasMoreData={queryList.hasNextPage}
            onLoadMore={async () => queryList.fetchNextPage()}
          />
        </VBox>
      </Modal>

      <Modal
        ignoreOverlayClick
        title={editMode ? 'Editar grupo' : 'Novo grupo'}
        show={!!editObject}
        onClose={handleCloseInner}
        onCancel={handleCloseInner}
        onConfirm={handleChange}
      >
        <FormGrupoUsuario
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors}
        />
      </Modal>
    </>
  );
};

export default GruposManagerModal;
